import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, HelpTip, Switch } from '@/components/ui';
import { api } from '@/lib/api';

/**
 * 🔴 O OUVIDO DO BOT — a tela do receptor de mensagens (#260).
 *
 * ⚠️ **Esta é a única tela do v2 que liga uma porta para a internet**, e o
 * desenho dela segue disso:
 *
 *   · o **desligar vem antes do ligar**, porque quem liga algo assim precisa
 *     saber sair antes de entrar (ordem literal do adendo);
 *   · a URL **não aparece sozinha**: é preciso pedir, e pedir é o gesto que
 *     gera o segredo;
 *   · escutar e **responder** são dois interruptores, e o segundo nasce
 *     desligado — o v2 sobe ouvindo e calado.
 */

interface Ouvido {
  url: string;
  evento: string;
  respondendo: boolean;
  recebidasHoje: number;
}

/**
 * ⚠️ 12/09 (#280): estes textos apontavam para a "Trilha de mensagens", que
 * mostra o que SAIU. O que CHEGOU ganhou aba própria — e o cartão passou a
 * apontar para ela. **Texto que aponta para um lugar tem de mudar quando o
 * lugar muda**: é a régua que o #261 deixou.
 */
export function OuvidoDoBot({ onMudou }: { onMudou?: () => void }) {
  const [dados, setDados] = useState<Ouvido | null>(null);
  const [mostrandoUrl, setMostrandoUrl] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(() => {
    setErro(null);
    api
      .get<Ouvido>('/api/v1/admin/whatsapp/ouvido')
      .then(setDados)
      .catch((e: Error) => setErro(e.message));
  }, []);

  useEffect(carregar, [carregar]);

  async function alternarResposta(ligado: boolean) {
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await api.post('/api/v1/admin/whatsapp/ouvido/responder', { ligado });
      setAviso(
        ligado
          ? 'Resposta automática LIGADA. Ela ainda passa por todas as portas de sempre: o cliente precisa estar liberado para este aviso na ficha dele, e hoje nenhum está.'
          : 'Resposta automática desligada. O sistema continua recebendo e registrando — só não fala.',
      );
      carregar();
      onMudou?.();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="border-t border-line pt-4 dark:border-line-dark" data-testid="wa-ouvido">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          O ouvido do bot
          <HelpTip texto="Até aqui o IGM só falava no WhatsApp. Ligando isto, ele também escuta os grupos em que o bot está — e pode responder a quem escreve fora do horário de atendimento. Nada do que chega é guardado além do grupo, da hora e do que foi decidido." />
        </span>
        {dados && (
          <Badge variant={dados.respondendo ? 'warning' : 'neutral'} size="sm">
            {dados.respondendo ? 'Ouvindo e respondendo' : 'Ouvindo, calado'}
          </Badge>
        )}
      </div>

      {erro && (
        <Alert variant="danger" className="mt-3">
          {erro}
        </Alert>
      )}
      {aviso && (
        <Alert variant="info" className="mt-3">
          {aviso}
        </Alert>
      )}

      {/* 🔴 O DESLIGAR VEM PRIMEIRO. Quem liga algo assim precisa saber sair
          antes de entrar — e a saída não depende do v2 estar de pé. */}
      <Alert variant="warning" className="mt-3" data-testid="wa-ouvido-desligar">
        <b>Para desligar, a qualquer momento:</b> no painel da Evolution, em{' '}
        <i>Webhook</i>, desmarque <b>Enabled</b> e salve. O sistema para de receber no mesmo instante.
        Puxar o freio de emergência aqui em cima também impede qualquer resposta, mas o sistema continua
        recebendo.
      </Alert>

      <div className="mt-3 space-y-3 text-sm text-ink-2 dark:text-ink-dark-2">
        <div>
          <p className="font-medium text-ink-1 dark:text-ink-dark-1">Para ligar, na Evolution:</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-xs text-ink-3 dark:text-ink-dark-3">
            <li>Abra o painel da Evolution, na instância do WhatsApp da IGM.</li>
            <li>
              Vá em <i>Webhook</i> e cole o endereço abaixo no campo <b>URL</b>.
            </li>
            <li>
              Marque <b>Enabled</b> e assine <b>somente</b> o evento{' '}
              <code className="rounded bg-paper-2 px-1 dark:bg-paper-dark-2">
                {dados?.evento ?? 'messages.upsert'}
              </code>
              .
            </li>
            <li>Salve. A partir daí o sistema recebe tudo, de todos os grupos em que o bot está.</li>
          </ol>
        </div>

        {/* ⚠️ A URL não fica à mostra: pedir é um gesto, e é o gesto que gera o
            segredo. Assim ele não vaza numa captura de tela distraída. */}
        {!mostrandoUrl ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setMostrandoUrl(true)}
            data-testid="wa-ouvido-mostrar"
          >
            Mostrar o endereço
          </Button>
        ) : (
          <div>
            <code
              className="block break-all rounded-lg border border-line bg-paper-2 p-2 text-xs dark:border-line-dark dark:bg-paper-dark-2"
              data-testid="wa-ouvido-url"
            >
              {dados?.url ?? '…'}
            </code>
            <p className="mt-1 text-xs text-red">
              Este endereço tem um segredo dentro. Quem o tiver consegue mandar eventos para o IGM —
              não cole em conversa, e-mail ou documento compartilhado.
            </p>
          </div>
        )}

        {dados && (
          <p className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid="wa-ouvido-hoje">
            {dados.recebidasHoje === 0
              ? 'Nada chegou hoje. Se você acabou de ligar na Evolution, escreva no grupo de teste e recarregue.'
              : `${dados.recebidasHoje} ${dados.recebidasHoje === 1 ? 'mensagem chegou' : 'mensagens chegaram'} hoje. O detalhe está na aba “O que chegou”.`}
          </p>
        )}
      </div>

      {/* 🔴 O SEGUNDO interruptor, e ele nasce desligado. */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-line p-3 dark:border-line-dark">
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-sm text-ink-1 dark:text-ink-dark-1">
            Responder quem escreve fora do horário
            <HelpTip texto="Com isto ligado, quem escrever num grupo de cliente fora do expediente recebe a mensagem do aviso 'Fora do horário'. Uma resposta por grupo, por período fora do expediente — se dez moradores escreverem na mesma noite, sai uma só. O aviso ainda precisa estar ligado, o cliente liberado na ficha dele e o freio solto." />
          </span>
          <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
            {dados?.respondendo
              ? 'Ligado. A resposta ainda passa pelo aviso "Fora do horário": o cliente precisa estar liberado na ficha dele.'
              : 'Desligado de propósito. Confira antes, na aba “O que chegou”, que está chegando o que devia e sendo descartado o que devia.'}
          </p>
        </div>
        <Switch
          checked={Boolean(dados?.respondendo)}
          disabled={salvando || !dados}
          label={dados?.respondendo ? 'Ligado' : 'Desligado'}
          onChange={(e) => void alternarResposta(e.target.checked)}
          data-testid="wa-ouvido-responder"
        />
      </div>
    </div>
  );
}
